import { Link } from 'react-router-dom'
import { useAdminBalance } from '@/entities/admin'
import type { AdminBalanceProvider } from '@/shared/api/types'
import { ElevenLabsCost } from '../shared/ElevenLabsCost'
import { formatUsd } from '../shared/adminCosts'
import {
  balanceStatusTone,
  isCompleteBalanceResponse,
  providerDisplayName,
  providerSnapshotAvailable,
  providerStatusLabel,
} from '../balance/balanceModel'
import styles from './AdminSummaryPage.module.css'

const WIDGET_PROVIDERS = ['openrouter', 'elevenlabs'] as const

export function AdminBalanceWidget() {
  const balance = useAdminBalance(30)

  return (
    <section className={styles.balanceWidget} aria-labelledby="summary-balance-title">
      <div className={styles.balanceWidgetHeader}>
        <div>
          <h2 id="summary-balance-title" className={styles.sectionTitle}>
            Баланс провайдеров
          </h2>
          <p className={styles.balanceWidgetHint}>Остатки и состояние счетов</p>
        </div>
        <Link className={styles.balanceWidgetLink} to="/admin/balance">
          Подробнее →
        </Link>
      </div>

      {balance.isPending ? (
        <p className={styles.balanceWidgetState}>Загружаем остатки…</p>
      ) : balance.isError || !balance.data ? (
        <div className={styles.balanceWidgetError} role="alert">
          <span>Баланс сейчас недоступен.</span>
          <button type="button" onClick={() => void balance.refetch()}>
            Повторить
          </button>
        </div>
      ) : !isCompleteBalanceResponse(balance.data) ? (
        <div className={styles.balanceWidgetError} role="alert">
          <span>Сервер вернул неполные данные баланса.</span>
          <button type="button" onClick={() => void balance.refetch()}>
            Повторить
          </button>
        </div>
      ) : balance.data.providers.length === 0 ? (
        <p className={styles.balanceWidgetState}>Провайдеры ещё не прислали данные.</p>
      ) : (
        <div className={styles.balanceWidgetRows}>
          {WIDGET_PROVIDERS.map((name) => {
            const provider = balance.data.providers.find(
              (item) => item.provider.toLowerCase() === name,
            )
            return provider ? (
              <ProviderRow
                key={name}
                provider={provider}
                rate={balance.data.elevenLabsUsdPer1kCredits}
              />
            ) : (
              <div key={name} className={styles.balanceWidgetRow} role="alert">
                <span className={`${styles.balanceDot} ${styles.balanceDotDanger}`} aria-hidden="true" />
                <span className={styles.balanceWidgetProvider}>
                  <strong>{providerDisplayName(name)}</strong>
                  <small>Недоступен</small>
                </span>
                <span className={styles.balanceWidgetValue}>Нет данных</span>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

function ProviderRow({ provider, rate }: { provider: AdminBalanceProvider; rate: number }) {
  if (!providerSnapshotAvailable(provider)) {
    return (
      <div className={styles.balanceWidgetRow} role="alert">
        <span className={`${styles.balanceDot} ${styles.balanceDotDanger}`} aria-hidden="true" />
        <span className={styles.balanceWidgetProvider}>
          <strong>{providerDisplayName(provider.provider)}</strong>
          <small>Недоступен</small>
        </span>
        <span className={styles.balanceWidgetValue} data-admin-value>
          Нет данных
        </span>
      </div>
    )
  }

  const tone = balanceStatusTone(provider)
  const isElevenLabs = provider.provider.toLowerCase() === 'elevenlabs'
  const dotClass =
    tone === 'good'
      ? styles.balanceDotGood
      : tone === 'stale'
        ? styles.balanceDotWarn
        : styles.balanceDotDanger

  return (
    <div className={styles.balanceWidgetRow}>
      <span className={`${styles.balanceDot} ${dotClass}`} aria-hidden="true" />
      <span className={styles.balanceWidgetProvider}>
        <strong>{providerDisplayName(provider.provider)}</strong>
        <small>{providerStatusLabel(provider)}</small>
      </span>
      <span className={styles.balanceWidgetValue} data-admin-value>
        {isElevenLabs ? (
          <ElevenLabsCost
            credits={provider.balance}
            usd={provider.balanceUsd}
            rate={rate}
          />
        ) : Number.isFinite(provider.balance) ? (
          `${formatUsd(provider.balance)} осталось`
        ) : (
          '$— осталось'
        )}
      </span>
    </div>
  )
}
